<template>
    <v-container
        :class="{
            'result-component pa-6 mx-md-12 invicta': true,
            'result-disabled': disabled,
            'result-compatible': compatible
        }"
    >
        <div class="result-compatible-label px-2 py-1" v-if="compatible">
            {{ $t('donor.phenotype.best_match') }}
        </div>
        <div v-if="disabled" class="notification-wrapper mb-4">
            <Notification type="cancel" :text="$t('results.material_unavailable')"/>
        </div>
        <div class="photo-wrapper d-flex flex-md-column flex-row justify-space-between justify-md-start w-100">
            <img :src="placeholder" class="photo mb-md-2" />
            <div class="id-wrapper d-flex flex-column text-left text-md-center flex-grow-1 ml-md-0 ml-4 justify-start">
                <div class="result_header mb-2">
                    {{$t(`donor.${phenotypeData.material.type}.nom`)}}
                </div>
                <div class="d-flex justify-md-center justify-start align-center">
                    <div class="id">
                        {{ phenotypeData.donor_process.donor_nickname }}
                    </div>
                    <v-icon  @click="toggleFavourite" :icon="isFavourite ? 'mdi-heart' : 'mdi-heart-outline'" :size="24" class="ml-md-1 ml-4" color="primary"></v-icon>
                </div>
            </div>
        </div>
        <div class="data-row-1 mb-md-0 mb-4">
            <div class="result_header d-flex flex-row">
                <span class="header_label flex-grow-1 text-left my-2 my-md-0">{{$t('donor.phenotype.physical_features_label')}}</span>
            </div>
            <div class="data-wrapper d-flex flex-row">
                <div class="data-label flex-grow-1 text-left">{{$t('donor.phenotype.eye_color')}}</div>
                <div class="data-value flex-grow-1 text-left text-capitalize">{{ $t('phenotype.eye_color.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.eye_color) }}</div>
            </div>
            <div class="data-wrapper d-flex flex-row">
                <div class="data-label flex-grow-1 text-left">{{$t('donor.phenotype.hair_color')}}</div>
                <div class="data-value flex-grow-1 text-left text-capitalize">
                    {{$t('phenotype.hair_color.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.hair_color).match(/([^\()]+)/)[0] }}<br/>
                    <span class="sub-entry" v-if="$t('phenotype.hair_color.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.hair_color).match(/\((.*?)\)/)">{{$t('phenotype.hair_color.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.hair_color).match(/\((.*?)\)/)[0]}}</span> 
                </div>
            </div>
            <div class="data-wrapper d-flex flex-row">
                <div class="data-label flex-grow-1 text-left">{{$t('donor.phenotype.height')}}</div>
                <div class="data-value flex-grow-1 text-left">{{ phenotypeData.phenotype.height }} {{$t('cm')}}</div>
            </div>
            <div class="data-wrapper d-flex flex-row">
                <div class="data-label flex-grow-1 text-left">{{$t('donor.phenotype.blood_type')}}</div>
                <div class="data-value flex-grow-1 text-left">{{ $t('phenotype.blood_type.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.blood_type) }} {{ $t('phenotype.blood_rh_factor.' + phenotypeData.material.type + '.' + phenotypeData.phenotype.blood_rh_factor) }}</div>
            </div>
            <div class="result_header d-flex flex-row mt-6">
                <span class="header_label flex-grow-1 text-left my-2 my-md-0">{{$t('donor.location_header')}}</span>
            </div>
            <div class="data-wrapper d-flex flex-row">
                <div class="data-label flex-grow-1 text-left">{{$t('donor.location_label')}}</div>
                <div class="data-value flex-grow-1 text-left">{{$t(phenotypeData.material.location.city)}}</div>
            </div>
        </div>
        <div class="data-row-2">
            <div class="mb-2 w-100 panel" v-if="oidcUser">
                <compare-to-donor-component 
                    :donor_phenotype="phenotypeData"
                    :donor_nickname="phenotypeData.donor_process.donor_nickname"
                    :donor_product_key="phenotypeData.id"
                    :donor_material_type="phenotypeData.material.type"
                    :user_id=null
                    :oidc_user="oidcUser"
                    :key="phenotypeData.donor_process.donor_nickname + refresher">
                </compare-to-donor-component>
            </div>
            <div class="compare_list_checkbox panel">
                <div class="pa-6 d-flex text-left button-add justify-space-between align-center"
                        @click="toggleCompareListItem($event)">
                    <img src="@/assets/img/add_to_compare_img.svg" class="mr-6"/>
                    <div class="mr-6 flex-grow-1">
                        <div class="panel_header">{{ $t("donor.phenotype.add_to_compare") }}</div>
                        <div class="panel_subheader" v-html="$t('donor.phenotype.add_to_compare_label')"></div>
                    </div>
                    <label class="container">
                        <input type="checkbox"
                            :checked="isInCompareList"
                        >
                        <span class="checkmark ml-2 mt-n3"></span>
                    </label>
                </div>
            </div>
        </div>
        <div class="bottom-bar invicta mt-2">
            <div class="d-flex flex-md-row flex-column align-md-center align-start" v-if="!disabled">
                <div class="d-flex flex-row w-100">
                    <v-spacer v-if="!$vuetify.display.mobile"></v-spacer>
                    <v-btn
                        elevation="0"
                        @click="goToDetails"
                        class="button-details mt-0 mr-0"
                        rounded
                        variant="plain"
                        height="40"
                        color="primary"
                        flat
                    >
                        {{$t('donor.details.details_btn')}}
                    </v-btn>
                    <v-spacer v-if="$vuetify.display.mobile"></v-spacer>
                    <div class="text-center" v-if="oidcUser">
                        <v-dialog
                            v-model="dialogOpen"
                            :fullscreen="$vuetify.display.mobile"
                        >
                            <template v-slot:activator="{ attrs }">
                                <v-btn plain elevation="0"
                                    v-bind="attrs"
                                    @click="showDialog"
                                    class="button-make-reservation ml-md-4"
                                    color="primary"
                                    variant="plain"
                                    height="40"
                                    :width="$vuetify.display.mobile ? '100%' : '156'"
                                    rounded
                                    flat
                                > 
                                    {{$t('reservation.reserve_btn')}}
                                </v-btn>
                            </template>
                            <div class="close" :class="{'mobile': $vuetify.display.mobile}" @click="dialogOpen = false">
                                <v-icon color="primary">mdi-close</v-icon>
                            </div>
                            <add-to-cart-component
                                :donor-details="phenotypeData"
                                :maxTrueReservationCount="phenotypeData.max_true_reservation_count[0]"
                                :dialog="true"
                                :age="this.phenotypeData.age[0]"
                                @close="hideDialog"
                            >
                            </add-to-cart-component>
                        </v-dialog>
                    </div>
                </div>
            </div>
            <div
                class="d-flex flex-md-row flex-column align-md-center align-start justify-end"
                :class="!$vuetify.display.mobile ? '' : 'align-center justify-center'"
                v-else
            >
                <Button
                    :round="true"
                    :uppercase="true"
                    type="danger"
                    class="info-button text-uppercase"
                    :class="!$vuetify.display.mobile ? '' : 'w-100'"
                    @click="toggleFavourite"
                >
                <v-icon>mdi-trash-can-outline</v-icon>{{ $t('results.favorite_button_remove') }}
                </Button>
            </div>
        </div>
    </v-container>
</template>

<script>
import { computed } from 'vue'
import { useStore, mapGetters } from 'vuex'
import axios from "axios";
import AddToCartComponent from './AddToCartComponent.vue';
import CompareToDonorComponent from './CompareToDonorComponent.vue';
import { Button, Notification } from 'design-system-ui-components'

export default {
    data() {
        return {
            dialogOpen: false,
            prices: null,
            refresher: 0,
            store: useStore()
        }
    },
    components: {
        AddToCartComponent,
        CompareToDonorComponent,
        Button, Notification
    },
    props: {
        phenotypeData: Object,
        disabled: {
            type: Boolean,
            default: false
        },
        compatible: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        phenotypeData() {
            this.refresher++;
        }
    },
    computed: {
        ...mapGetters('favouritesModule', ['getFavourites']),
        ...mapGetters('compareModule', ['getCompareList', 'getCompareListByMaterialType']),
        isFavourite() {
            return this.getFavourites.find(e => e.donor_product_key == this.phenotypeData.id && e.material_type == this.materialType);
        },
        isInCompareList() {
            return this.getCompareListByMaterialType(this.materialType).find(e => e.pk == this.phenotypeData.id);
        }
    },
    setup(props) {
        const store = useStore()
        
        const placeholder = computed({
            get: () => {
                if(props.phenotypeData.material.type == "mtCell")
                    return require('../assets/img/placeholder_female.svg')
                else
                    return require('../assets/img/placeholder_male.svg')
            }
        })

        const materialType = computed({
            get: () => {return props.phenotypeData.material.type;}
        })

        const eye_icon = computed({
        get: () => { return require('../assets/icons/eye.svg');}
        })
        const blood_icon = computed({
        get: () => { return require('../assets/icons/blood.svg');}
        })
        const group_icon = computed({
        get: () => { return require('../assets/icons/group.svg');}
        })
        return {
            placeholder,
            eye_icon,
            blood_icon,
            group_icon,    
            materialType,
            oidcUser: computed(() => store.getters['oidcStore/oidcUser']),
        }
    },
    mounted() {
        axios
            .get(`/api/donor/material_price`)
            .then((response) => {
                this.prices = response.data
            })
            .catch(err => {
                console.error(err);
            })
    },
    methods: {
        goToDetails() {
            const materialType = this.phenotypeData.material.type == 'mtCell' ? 'eggs' : 'sperm';
            const id = this.phenotypeData.id;
            if(!this.oidcUser){
                this.$router.push({
                    name: 'redirect',
                    params: {
                        mode: 'login',
                        pathname: `/details/${materialType}/${id}`
                    }
                })
            } else {
                this.$router.push({
                    name: 'donorDetails',
                    params: {
                        materialType, id
                    }
                })
            }
            window.scrollTo({ top: 0 })
        },
        redirect() {
            this.$router.push({
                name: 'redirect',
                params: {
                    mode: 'login',
                    pathname: location.pathname
                }
            })
        },
        showDialog() {
            this.dialogOpen = true;
        },
        hideDialog() {
            this.dialogOpen = false;
        },
        sendBookingRequest(){
        // TODO: send e-mail to user requesting booking of material
            this.$root.$alertMessageRef.showAlert(
                this.$t(
                    `alert.BOOKING_MANUAL_SUCCESS.title`
                ),
                null,
                {
                    action: false,
                    link: false,
                    timeout: true,
                    timeoutDuration: 5000,
                    color: '#6BD783',
                    backgroundColor: 'rgba(107, 215, 131, 0.2)',
                    icon: 'mdi-check-circle-outline',
                    iconColor: '#6BD783',
                    width: '800px'
                }
            )
        },
        toggleFavourite(){
            if(this.oidcUser){
                if(this.isFavourite){
                    this.store.dispatch('favouritesModule/removeFavourite', {
                        donor_product_key: this.phenotypeData.id,
                        material_type: this.materialType
                    }).then(() => {
                        this.$gtag.event('donor', {
                            'donor_product_key': this.phenotypeData.id,
                            'donor_material_type': this.materialType,
                            'donor_event_value': 'remove from favourites'
                        })
                    })
                } else {
                    this.store.dispatch('favouritesModule/addFavourite', {
                        donor_product_key: this.phenotypeData.id,
                        material_type: this.materialType
                    }).then(() => {
                        this.$gtag.event('donor', {
                            'donor_product_key': this.phenotypeData.id,
                            'donor_material_type': this.materialType,
                            'donor_event_value': 'add to favourites'
                        })
                    })
                }
            }
            
        },
        toggleCompareListItem(e){
            this.$store.dispatch(
                'compareModule/toggleCompareListItem',
                {
                    id: this.phenotypeData.donor_process.donor_nickname,
                    pk: this.phenotypeData.id,
                    materialType: this.materialType})
            .then(result => {
                if(!result) {
                    this.$root.$alertMessageRef.showAlert(
                        this.$t(`alert.COMPARE_LIST_FULL.title`, {}),
                        this.$t(`alert.COMPARE_LIST_FULL.message`, {}),
                        {
                            link: false,
                            backgroundColor: 'rgb(254,245,228)',
                            borderColor: 'rgb(255, 230, 100)',
                            timeout: true,
                            timeoutDuration: 5000,
                            iconColor: 'rgba(249, 193, 0, 1)',
                            icon: 'mdi-information-outline',
                            width: 'auto'
                        }
                    )
                    e.preventDefault();
                }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../node_modules/design-system-ui-components/dist/all.min.css";

// * {
//     box-shadow: 0px 0px 1px rgb(151, 151, 255) inset;
// }

.panel {
    border-radius: 12px;
    border: 1px solid transparent !important;
    cursor: pointer;
}
.panel:hover {
    border: 1px solid var(--nsb-color-1, #0E346F) !important;
}
.panel_header {
    @include ds-font-label-medium;
    color: var(--nsb-color-1, #0E346F);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px; /* 157.143% */
}
.panel_subheader {
    @include nsb-font-label;
    color: var(--nsb-color-1, #0E346F);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 150% */
}
.result_header {
    color: var(--bank-primary, #00205B);
    @include nsb-gentium-bold;
    font-size: 23px;
    font-style: normal;
    // font-weight: 700;
    line-height: 145%; /* 33.35px */
}

.result-disabled {
    border: 1px solid $ds-error;
    .photo-wrapper {
        pointer-events: all;
        cursor: pointer;
    }
}

.button-add {
    @include nsb-font-container;
    font-size: 14px !important;
    opacity: 1;
    border-radius: 12px;
    background: var(--bank-secondary-300, #E3ECF8);
  }
  .button-add:hover {
    border: none !important;
    box-shadow: none !important;
  }
.result-component {
    position: relative;
    border: 2px solid transparent;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: repeat(5, auto);
    grid-column-gap: 8px;
    grid-row-gap: 8px;
    border-radius: 4px;
    // padding: 24px;
    background-color: $ds-white;
    box-shadow: 0px 8px 16px 0px rgba(112, 144, 176, 0.20);

    .notification-wrapper { grid-area: 1 / 1 / 2 / 3; }
    .photo-wrapper        { grid-area: 2 / 1 / 3 / 3; }
    .id-wrapper           { grid-area: 2 / 2 / 3 / 3; }
    .data-row-1           { grid-area: 3 / 1 / 4 / 3; }
    .data-row-2           { grid-area: 4 / 1 / 5 / 3; }
    .bottom-bar           { grid-area: 5 / 1 / 6 / 3; }

    .id-wrapper {
        .label {
            @include nsb-font-header;
            font-size: 14px;
            line-height: 21px;
            color: $ds-primary;
        }
        .id {
            @include nsb-font-container;
            font-size: 14px;
            line-height: 21px;
            color: $ds-primary;
            border-radius: 12px;
            background-color: $ds-primary-9;
            display: inline-block;
            padding: 1.5px 12px;
            white-space: nowrap;
        }
    }
    .photo-wrapper {
        position: relative;
        background: white;
        padding: 0px;

        .icon {
            display: block;
            width: 24px !important;
            height: 24px !important;
            position: relative;
            top: 7px;
            right: 0px;
            cursor: pointer;
            color: $nsb-color-9;
        }

        .photo {
            width: 135px;
            margin-right: 16px;
        }
    }
    .header {
        @include nsb-font-container-medium;
        font-size: 14px;
        line-height: 21px;
        color: $ds-primary;
    }
    .data-wrapper {
        @include nsb-font-container;
        font-size: 14px;
        line-height: 21px;
        margin: 8px auto;
        .data-label {
            width: 50%;
            color: $ds-primary-3;
        }
        .data-value {
            width: 50%;
            color: $ds-primary;
        }
    }
    .sub-entry {
        font-size: 12px;
        color: $ds-primary;
    }
    .button-make-reservation {
        opacity: 1;
        border: 1px solid $ds-primary;
    }
    .button-details {
        opacity: 1;
        border: 1px solid transparent;
    }

    :deep(.notification) {
        background-color: $ds-error-4;
        .notification--text {
            @include nsb-font-container;
            color: $ds-primary;
            font-size: 14px;
            line-height: 23px;
        }
    }
    &.result-compatible {
        border: 2px solid var(--bank-secondary-200, #9EC4F2);
    }
    .result-compatible-label {
        @include ds-font-label-medium;
        position: absolute;
        top: -15px;
        right: 24px;
        border-radius: 4px;
        background: var(--bank-secondary-200, #9EC4F2);
        color: var(--bank-primary, #00205B);
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
    }
}
:deep(.v-card .cart_container) {
    padding: 0px !important;
    margin:  0px !important;
}
.close{
    cursor: pointer;
    position: fixed;
    top: 22px;
    right: 30px;
    z-index: 10;
    &.mobile {
        right: 30px;
    }
}
.modal-header {
    @include nsb-font-header-bold;
    font-size: 22px !important;
    line-height: 33px !important;
}
.modal-body {
    background: $nsb-disabled;
    &-mobile {
        background: white;
    }
    @include nsb-font-container-medium;
    font-size: 14px;
    line-height: 21px;
    .bold {
        @include nsb-font-header-bold;
        font-size: 14px;
        line-height: 21px;
    }
}
.buttonWrapper {
    background: $nsb-disabled;
    &-mobile {
        background: white;
    }
    .v-btn {
        @include nsb-font-container;
        font-size: 12px !important;
        line-height: 18px;
    }
}

@media (min-width: 960px) {
    .result-component {
        width: unset;
        padding: 24px;
        border-radius: 4px;

        display: grid;
        grid-template-columns: auto 1fr 1.3fr;
        grid-template-rows: auto 1fr auto;
        grid-column-gap: 8px;
        grid-row-gap: 8px;
        
        background: white;
        
        .notification-wrapper { grid-area: 1 / 1 / 2 / 4; }
        .photo-wrapper        { grid-area: 2 / 1 / 3 / 2; }
        .id-wrapper           { grid-area: 3 / 1 / 4 / 2; }
        .data-row-1           { grid-area: 2 / 2 / 4 / 3; }
        .bottom-bar           { grid-area: 4 / 1 / 5 / 4; }
        .data-row-2           { grid-area: 2 / 3 / 4 / 4; }

        .id-wrapper {
            max-width: 135px;
        }
    }

}
.input-label {
    @include nsb-font-container;
      font-size: 14px;
      line-height: 17px;
      cursor: pointer;
      color: $ds-primary;
}

/* The container */
.container {
  display: block;
  position: relative;
  padding-left: 24px;
  cursor: pointer;
  user-select: none;
    @include nsb-font-button-semibold;
    font-size: 14px !important;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  box-sizing: border-box;
  border: 1px solid #869AB7;
  position: absolute;
  top: 2px;
  left: 0;
  height: 16px;
  width: 16px;
  background-color: white;
  pointer-events: none;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: $nsb-disabled;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: $ds-primary;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 1px 1px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
</style>